import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import 'ol/ol.css';
import olMap from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'Common/Map/OpenLayers/Layer/TileLayer';
import VectorLayer from 'Common/Map/OpenLayers/Layer/VectorLayer';
import TssDetector from 'Common/Map/OpenLayers/Tss/TssDetector';
import CctvCameraIcon from 'Common/Map/OpenLayers/Cctv/CctvCamera';
import DmsSign from 'Common/Map/OpenLayers/Dms/DmsSign';
import Tooltip from 'Common/Map/OpenLayers/Overlay/Tooltip';
import TssLinkContainer from 'Common/Map/OpenLayers/Containers/TssLinkContainer';
import { fromLonLat } from 'ol/proj';
import { Camera, Dms, DetectorConfig, Link, Id, DetectorMap } from 'NSwag/ServerClient/Client.nswag';
import { ContextMenu } from 'Common/Map/OpenLayers/Menu/ContextMenu';
import { ConfigClient, ClientConfig } from 'NSwag/ThinClient/Client.nswag';
import { IHashMap } from 'DataLibrary/Common/IHashMap';
import ErrorDialog from 'Common/Dialog/ErrorDialog';

interface Props {
    cameras: IHashMap<Camera>;
    dmses: IHashMap<Dms>;
    tssDetectors: IHashMap<DetectorConfig>;
    tssDetectorMaps: IHashMap<DetectorMap>;
    tssLinks: IHashMap<Link>;
}

interface State {
    map: olMap | null;
    config: ClientConfig | null;
    error: string;
    zoomLevel: any;
}

/*
 * Define the actual component, its state if it has one
 * and its logic
 */
class OpenLayersMap extends React.Component<Props, State> {
    id: Id;

    contextMenu: ContextMenu | null;

    tooltip: Tooltip | null;

    /**
    * Set the initial state
    * @param { object } props the set of properties associated with this component
    * @return { void }
    */
    constructor(props: Props) {
        super(props);
        this.id = new Id({
            name: 'OpenLayersMap',
            resourceType: 'OpenLayersMap',
            centerId: 'OL',
            providerName: 'OL'
        });

        this.contextMenu = null;
        this.tooltip = null;

        this.state = {
            map: null,
            error: '',
            config: null,
            zoomLevel: 8
        };
    }

    /**
    * checks to see if the component has been mounted yet, if so start rendering Tss icons
    * @return { type } description
    */
    componentDidMount = () => {
        this.createOpenLayersMap();
    }

    /**
     * Method fired before component re-renders to determine if it should re-render or not
     * @returns {boolean} - true if component needs to be re-rendered; false if not.
     */
    shouldComponentUpdate = (nextProps: Props, nextState: State) => {
        const retVal = true;
        return retVal;
    }

    /**
     * Method fired after a component has re-rendered.
     */
    componentDidUpdate = () => {
        if (this.state.map == null && this.state.error === '') {
            this.createOpenLayersMap();
        }
    }

    componentWillUnmount = () => {
        if (this.tooltip) {
            this.tooltip.cleanup();
        }
    }

    /**
    * creates an open layers map
    * @return { OlMap } a new open layers map
    */
    createOpenLayersMap = () => {
        // transforms these points to fit our projection.
        this.loadConfig().then(config => {
            const pts = fromLonLat([config.mapConfig.center.long, config.mapConfig.center.lat]);

            // eslint-disable-next-line new-cap
            const map = new olMap({
                target: 'map-container',
                layers: [],
                view: new View({
                    center: [pts[0], pts[1]],
                    zoom: 8
                })
            });

            this.contextMenu = new ContextMenu({ map });
            this.tooltip = new Tooltip({ map });
            map.on('moveend', this.mapChanged);
            this.setState({ config, map });
        }).catch(error => {
            this.setState({ error: 'Unable to load map configuration.' });
        });
    }

    loadConfig = (): Promise<ClientConfig> => {
        // ToDo: This should be removed and the client configuration should \
        // either be added to the store or preferably as a context using the
        // new react API.
        return new Promise<ClientConfig>((resolve, reject) => {
            const client = new ConfigClient(window.location.href.replace('/map', ''));
            client.config()
                .then(config => {
                    resolve(config);
                }).catch(error => {
                    reject(error);
                });
        });
    }

    /**
     * Method fired if map changes to set zoomLevel
     */
    mapChanged = (evt: any) => {
        const map = evt.map;
        const zoomLevel = map.getView().getZoom();
        if (zoomLevel !== this.state.zoomLevel) {
            this.setState({ zoomLevel });
        }
    }

    /**
    * renders the open layers map
    * @return { type } returns a list of children that have been rendered
    */
    render() {
        const { tssDetectors, tssDetectorMaps, tssLinks, cameras, dmses } = this.props;
        const { map, zoomLevel, error, config } = this.state;

        return (
            <div>
                <div id="map-container" style={{ position: 'fixed', height: '100%', width: '100%', backgroundColor: '#99B3CC' }}>
                    {
                        map && config &&
                        <div>
                            <TileLayer
                                map={map}
                                config={config}
                            />
                            <VectorLayer
                                map={map}
                                name="VectorLayer0"
                            >
                                {
                                    Object.keys(tssDetectors).map((key: string) =>
                                        <TssDetector
                                            key={key}
                                            detector={tssDetectors[key]}
                                            zoomLevel={zoomLevel}
                                        />)
                                }
                                {
                                    Object.keys(tssLinks).map(key =>
                                        <TssLinkContainer
                                            key={key}
                                            link={tssLinks[key]}
                                            zoomLevel={zoomLevel}
                                        />)
                                }
                                {
                                    Object.keys(cameras).map((key: string) =>
                                        <CctvCameraIcon key={key}
                                            map={map}
                                            camera={cameras[key]}
                                            zoomLevel={zoomLevel}
                                        />)
                                }
                                {
                                    Object.keys(dmses).map((key: string) =>
                                        <DmsSign key={key}
                                            map={map}
                                            dms={dmses[key]}
                                            zoomLevel={zoomLevel}
                                        />)
                                }
                            </VectorLayer>
                        </div>
                    }
                </div>
                {
                    error && error.length > 0 &&
                    <ErrorDialog
                        error={error}
                        onClose={() => this.setState({ error: '' })}
                    />
                }
            </div>
        );
    }
}

/*
 * Make our component the default export object and attach the styles defined
 */
export default OpenLayersMap;
